import {observer} from "mobx-react";
import {useContext} from "react";
import {Button, Divider, Typography} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import {Frame} from "../common/Frame";
import {RecordingStoreContext} from "../../stores/RecordingStore";
import {recording} from "../../proto/compiled";
import {RecordingAnnotationItem} from "./RecordingAnnotationItem";

interface RecordingAnnotationsSidebarProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
}

const annotationKey = (annotation: recording.IRecordingAnnotation, index: number) =>
  annotation.id && annotation.id.length
    ? annotation.id
    : (annotation as any)._clientId ?? `annotation-${index}`;

export const RecordingAnnotationsSidebar = observer(({videoRef}: RecordingAnnotationsSidebarProps) => {
  const store = useContext(RecordingStoreContext);
  const theme = useTheme();

  if (!store) {
    return null;
  }

  return (
    <Frame
      direction="column"
      width={50}
      padding={2.5}
      appearance={'none'}
      style={{
        borderRight: `1px solid ${theme.palette.divider}`,
      }}
      scroll
    >
      <Frame row justifyContent="space-between" alignItems="center" marginBottom={2}>
        <Typography variant="h4">Annotations</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon/>}
          disabled={store.hasUnsavedAnnotation}
          onClick={() => {
            const annotation = store.createAnnotation(store.currentTimeMs);
            store.selectedAnnotation = annotation;
          }}
        >
          New
        </Button>
      </Frame>
      <Divider style={{marginBottom: 16}}/>
      <Frame direction="column" gap={1.5}>
        {store.annotations.length === 0 && (
          <Frame
            padding={3}
            radius={1.5}
            style={{
              backgroundColor: theme.palette.action.hover,
              border: `1px dashed ${theme.palette.divider}`,
            }}
          >
            <Typography variant="body2" color="text.secondary" textAlign="center">
              No annotations yet. Click &#34;New&#34; to create one at the current playback time.
            </Typography>
          </Frame>
        )}
        {store.annotations.map((annotation, index) => (
          <RecordingAnnotationItem
            key={annotationKey(annotation, index)}
            annotation={annotation}
            videoRef={videoRef}
          />
        ))}
      </Frame>
    </Frame>
  );
});
